/* eslint-disable max-lines */
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerTitle,
} from '@/components/ui/mobile-responsive-dialog-drawer';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import {
  inlineMdComponents,
  streamdownRehypePlugins,
  type MarkdownComponents,
} from '@heidi/answer-kit';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { AlertCircle, Bug, Check, Info, Link2, Loader2 } from 'lucide-react';
import { Streamdown } from 'streamdown';
// Note: AlertCircle is still used in the error state
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useShareEvidenceChat } from './hooks/use-share-evidence-chat';
import type { ShareEvidenceChatModalProps } from './share-evidence-chat-modal.types';

// Social media icons as simple SVGs
const LinkedInIcon = () => (
  <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const XIcon = () => (
  <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const RedditIcon = () => (
  <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export function ShareEvidenceChatModal({
  isOpen,
  onClose,
  chatId,
  chatTitle,
  isStreaming = false,
}: ShareEvidenceChatModalProps) {
  const intl = useIntl();
  const { trackShareModalOpened } = useEvidenceAnalytics();
  const hasTrackedOpen = useRef(false);
  const {
    shareData,
    isCreatingShare,
    isCopied,
    debugInfo,
    createShare,
    copyLink,
    openSocialShare,
    copyDebugInfo,
  } = useShareEvidenceChat({ chatId });

  // Track modal open and create share when modal opens
  useEffect(() => {
    if (isOpen && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackShareModalOpened(chatId);
    }
    if (!isOpen) {
      hasTrackedOpen.current = false;
    }
  }, [isOpen, chatId, trackShareModalOpened]);

  // Create share when modal opens (only if not streaming)
  useEffect(() => {
    if (isOpen && !shareData && !isStreaming) {
      void createShare();
    }
  }, [isOpen, shareData, createShare, isStreaming]);

  const displayTitle = shareData?.chatTitle ?? chatTitle ?? 'Evidence Chat';

  return (
    <DialogDrawer open={isOpen} dismissible onOpenChange={(open) => !open && onClose()}>
      <DialogDrawerContent className="flex flex-col gap-5 p-6 sm:w-[550px]">
        <DialogDrawerTitle className="text-balance">
          <FormattedMessage defaultMessage="Share chat" id="bXa+lJhlYV" />
        </DialogDrawerTitle>

        <div className="flex items-start gap-2 rounded-lg bg-validation-info-muted px-3 py-2.5 text-validation-info">
          <Info size={14} className="mt-0.5 shrink-0" />
          <TypographyCaption>
            <FormattedMessage
              defaultMessage="By sharing, you confirm this content contains no sensitive or identifiable information."
              id="eMfyr2guer"
            />
          </TypographyCaption>
        </div>

        {/* Preview card - Chat style like ChatGPT */}
        <div className="flex flex-col rounded-xl border border-border bg-surface">
          {isStreaming ? (
            /* Waiting for response to complete */
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Loader2 size={24} className="animate-spin text-text-secondary" />
              <TypographyP2 className="text-text-secondary">
                <FormattedMessage
                  defaultMessage="Waiting for response to complete..."
                  id="2iZcc2czpe"
                />
              </TypographyP2>
            </div>
          ) : isCreatingShare ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-text-secondary" />
            </div>
          ) : debugInfo ? (
            /* Error state with debug info */
            <div className="flex flex-col gap-3 p-5">
              <div className="flex items-center gap-2 text-validation-destructive">
                <AlertCircle size={16} />
                <TypographyP2 className="font-medium">
                  <FormattedMessage
                    defaultMessage="Failed to create share link"
                    id="N6omPQO2Zj"
                  />
                </TypographyP2>
              </div>
              <TypographyCaption className="text-text-secondary">
                Error: {debugInfo.errorMessage}
                {debugInfo.errorStatus && ` (${debugInfo.errorStatus})`}
              </TypographyCaption>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void copyDebugInfo()}
                className="mt-1"
              >
                <Bug size={14} />
                <FormattedMessage defaultMessage="Copy debug info" id="dlmUd4bMJo" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void createShare()}
                className="text-text-secondary"
              >
                <FormattedMessage defaultMessage="Try again" id="FazwRldA7z" />
              </Button>
            </div>
          ) : (
            <>
              {/* Chat title with divider like ChatGPT */}
              <div className="border-b border-border px-5 py-4">
                <TypographyP2 className="text-lg font-semibold">
                  {displayTitle}
                </TypographyP2>
              </div>

              {/* Chat preview area */}
              <div className="flex max-h-[260px] flex-col gap-4 overflow-auto p-5">
                {/* User message bubble - aligned right */}
                {shareData?.previewQuestion && (
                  <div className="flex justify-end">
                    <div
                      className="max-w-[80%] rounded-3xl px-4 py-3"
                      style={{ backgroundColor: '#f4e7dd' }}
                    >
                      <TypographyP2 className="text-text-primary">
                        {shareData.previewQuestion}
                      </TypographyP2>
                    </div>
                  </div>
                )}

                {/* Assistant response - with light background */}
                {shareData?.previewResponse && (
                  <div className="flex justify-start">
                    <div
                      className="max-w-[90%] overflow-hidden rounded-2xl px-4 py-3"
                      style={{ backgroundColor: '#fdfaf7' }}
                    >
                      <Streamdown
                        components={inlineMdComponents as MarkdownComponents}
                        mode="static"
                        tableMaxHeight={0}
                        rehypePlugins={streamdownRehypePlugins}
                      >
                        {shareData.previewResponse}
                      </Streamdown>
                    </div>
                  </div>
                )}

                {/* Heidi branding at bottom right */}
                <div className="flex items-center justify-end pt-2">
                  <TypographyCaption className="font-medium text-text-tertiary">
                    Heidi Evidence
                  </TypographyCaption>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Copy link button (primary CTA) */}
        <Button
          size="lg"
          onClick={() => void copyLink()}
          disabled={isCreatingShare}
          className="w-full"
        >
          {isCreatingShare ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <FormattedMessage defaultMessage="Creating link..." id="/3PispA08S" />
            </>
          ) : isCopied ? (
            <>
              <Check size={16} />
              <FormattedMessage defaultMessage="Copied!" id="0oL1zz+YDx" />
            </>
          ) : (
            <>
              <Link2 size={16} />
              <FormattedMessage defaultMessage="Copy link" id="lbr3Lq2Lnm" />
            </>
          )}
        </Button>

        {/* Social share options */}
        <div className="flex items-center justify-center gap-6">
          <Button
            size="lg"
            variant="ghost"
            onClick={() => void openSocialShare('linkedin')}
            disabled={isCreatingShare}
            className="flex h-auto flex-col items-center gap-1.5 p-0 hover:bg-transparent hover:opacity-80"
            aria-label={intl.formatMessage({
              defaultMessage: 'Share on LinkedIn',
              id: 'M4kFT5MCLI',
            })}
          >
            <div className="flex size-10 items-center justify-center rounded-full border border-border bg-surface">
              <LinkedInIcon />
            </div>
            <TypographyCaption>
              <FormattedMessage defaultMessage="LinkedIn" id="Rb/hb951oE" />
            </TypographyCaption>
          </Button>
          <Button
            size="lg"
            variant="ghost"
            onClick={() => void openSocialShare('x')}
            disabled={isCreatingShare}
            className="flex h-auto flex-col items-center gap-1.5 p-0 hover:bg-transparent hover:opacity-80"
            aria-label={intl.formatMessage({
              defaultMessage: 'Share on X',
              id: 'QNvtaquhOJ',
            })}
          >
            <div className="flex size-10 items-center justify-center rounded-full border border-border bg-surface">
              <XIcon />
            </div>
            <TypographyCaption>
              <FormattedMessage defaultMessage="X" id="MXPwVkq5Ri" />
            </TypographyCaption>
          </Button>
          <Button
            size="lg"
            variant="ghost"
            onClick={() => void openSocialShare('reddit')}
            disabled={isCreatingShare}
            className="flex h-auto flex-col items-center gap-1.5 p-0 hover:bg-transparent hover:opacity-80"
            aria-label={intl.formatMessage({
              defaultMessage: 'Share on Reddit',
              id: 's8OCulrh4Y',
            })}
          >
            <div className="flex size-10 items-center justify-center rounded-full border border-border bg-surface">
              <RedditIcon />
            </div>
            <TypographyCaption>
              <FormattedMessage defaultMessage="Reddit" id="LzrJWgzbU1" />
            </TypographyCaption>
          </Button>
          <Button
            size="lg"
            variant="ghost"
            onClick={() => void openSocialShare('whatsapp')}
            disabled={isCreatingShare}
            className="flex h-auto flex-col items-center gap-1.5 p-0 hover:bg-transparent hover:opacity-80"
            aria-label={intl.formatMessage({
              defaultMessage: 'Share on WhatsApp',
              id: 'oCGtfV9ZuU',
            })}
          >
            <div className="flex size-10 items-center justify-center rounded-full border border-border bg-surface">
              <WhatsAppIcon />
            </div>
            <TypographyCaption>
              <FormattedMessage defaultMessage="WhatsApp" id="5ow6dgLgNa" />
            </TypographyCaption>
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
